import { Check } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

type Props = {
  email: string;
  name: string;
  password: string;
  passwordConfirmation: string;
  onNameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onPasswordConfirmationChange: (value: string) => void;
};

// "Set up your account" — confirm your name and set a real password. Email is
// fixed (came from the Slack sign-in that created this account), shown as a
// read-only identity chip so it's obvious which account this is.
export default function ProfileStep({
  email,
  name,
  password,
  passwordConfirmation,
  onNameChange,
  onPasswordChange,
  onPasswordConfirmationChange,
}: Props) {
  const longEnough = password.length >= 8;
  const mismatch = passwordConfirmation.length > 0 && password !== passwordConfirmation;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-base font-semibold text-foreground">Set up your account</h2>
        <p className="text-sm text-muted-foreground">Confirm your name and set a password.</p>
      </div>

      <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 px-4 py-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
          {email.charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-foreground">{email}</span>
          <span className="block text-xs text-muted-foreground">Your sign-in email, from Slack</span>
        </span>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="onboarding-name" className="text-sm font-medium text-foreground">Your name</label>
        <Input
          id="onboarding-name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          autoComplete="name"
          autoFocus
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="onboarding-password" className="text-sm font-medium text-foreground">Password</label>
        <Input
          id="onboarding-password"
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          autoComplete="new-password"
        />
        <p className={cn("flex items-center gap-1 text-xs", longEnough ? "text-success" : "text-muted-foreground")}>
          {longEnough && <Check size={12} />}
          At least 8 characters.
        </p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="onboarding-password-confirmation" className="text-sm font-medium text-foreground">
          Confirm password
        </label>
        <Input
          id="onboarding-password-confirmation"
          type="password"
          value={passwordConfirmation}
          onChange={(e) => onPasswordConfirmationChange(e.target.value)}
          autoComplete="new-password"
        />
        {mismatch && <p className="text-xs text-destructive">Passwords don't match.</p>}
      </div>
    </div>
  );
}
